import Fragment, { useEffect, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import SlidingPane from 'react-sliding-pane';
import Table from 'views/components/Table';
import AddButton from 'views/components/AddButton';
import EditButton from 'views/components/EditButton';
import DeleteButton from 'views/components/DeleteButton';
import Toolbar from 'views/components/Toolbar';
import useGetUnitSpecialFunctions from 'views/screens/Units/hooks/useGetUnitSpecialFunctions';
import unitPermissions from 'views/screens/TrackingUnits/permissions';
import { Row, Col } from 'reactstrap';
import SlidingPaneEditUnitSpecialFunctions from './SlidingPaneEditUnitSpecialFunctions';
import { deleteUnitSpecialFunctionService } from 'views/screens/Units/services';
import * as alerts from '../../../redux/utils/alerts';

interface Props {
  isOpenSlidingPane: boolean;
  closeSlidingPane: () => void;
  unitId: number;
  unitName: string;
  isOnMap?: boolean;
  width?: string;
}

const columns = [
  {
    fieldName: 'specialFunctionId',
    headerText: 'common.name',
    template: (value: any) => {
      const specialFunctionId = value.specialFunctionId;
      if (specialFunctionId) {
        return <FormattedMessage id={`units.specialFunction${specialFunctionId}`} />;
      } else null;
    }
  }
];

const SlidingPaneUnitSpecialFunctions = (props: Props) => {
  const { isOpenSlidingPane, closeSlidingPane, unitId, unitName, isOnMap = false, width } = props;

  const intl = useIntl();
  const [selected, changeSelected] = useState<any>([]);
  const [openEditSlidingPane, setOpenEditSlidingPane] = useState(false);
  const [refetch, setRefetch] = useState(true);

  const { specialFunctions, isFetching } = useGetUnitSpecialFunctions(unitId, refetch);

  useEffect(() => {
    if (!isFetching) {
      setRefetch(false);
    }
  }, [isFetching]);

  console.log('selected', selected);

  return (
    <>
      <SlidingPane
        className="custom-sliding-pane"
        overlayClassName={'custom-overlay-sliding-pane'}
        isOpen={isOpenSlidingPane}
        title={intl.formatMessage({ id: 'units.specialFunctions' })}
        subtitle={unitName}
        onRequestClose={closeSlidingPane}
        width={width ?? '50rem'}
      >
        <Row>
          <Col sm="12">
            <Toolbar
              buttons={[
                <AddButton
                  tooltipMessage={'units.specialFunctions'}
                  permissions={[unitPermissions?.addUnitSpecialFunctions]}
                  handleClick={() => {
                    changeSelected([]);
                    setOpenEditSlidingPane(true);
                  }}
                />,
                <EditButton
                  tooltipMessage={'units.specialFunctions'}
                  permissions={[unitPermissions?.editUnitSpecialFunctions]}
                  isDisabled={selected.length !== 1 || selected[0].specialFunctionId === 22}
                  handleClick={() => setOpenEditSlidingPane(true)}
                />,
                <DeleteButton
                  tooltipMessage={'units.specialFunctions'}
                  permissions={[unitPermissions?.deleteUnitSpecialFunctions]}
                  isDisabled={selected.length !== 1}
                  handleClick={() => {
                    deleteUnitSpecialFunctionService(
                      {
                        unitId: selected[0].unitId,
                        specialFunctionId: selected[0].specialFunctionId,
                        eventId: selected[0].eventId
                      },
                      {
                        successAction: (response) => {
                          if (response?.success) {
                            setRefetch(true);
                          } else {
                            alerts.showErrorAlertFailChangesToast({
                              error: intl.formatMessage(
                                { id: 'common.error' },
                                { unitName: unitName }
                              )
                            });
                          }
                        },
                        errorAction: (error) => {
                          alerts.showErrorAlertFailChangesToast({
                            error: intl.formatMessage(
                              { id: 'common.error' },
                              { unitName: unitName }
                            )
                          });
                        }
                      }
                    );
                  }}
                />
              ]}
            />
            <Table
              id={'unitSpecialFunctionsTable'}
              columns={columns}
              isFetching={isFetching}
              data={specialFunctions}
              selected={selected}
              changeSelected={changeSelected}
              enablePersistence={false}
            />
          </Col>
        </Row>
      </SlidingPane>
      {openEditSlidingPane && (
        <SlidingPaneEditUnitSpecialFunctions
          isOpenSlidingPane={openEditSlidingPane}
          closeSlidingPane={() => setOpenEditSlidingPane(false)}
          isNew={selected.length === 0}
          specialFunctionId={selected[0]?.specialFunctionId}
          initialValues={selected[0]}
          width={width}
          unitName={unitName}
          unitId={unitId ?? selected[0]?.unitId}
          refetchSpecialFunctions={() => setRefetch(true)}
          existingSpecialFunctionIds={
            selected.length === 0
              ? specialFunctions
                  .filter((specialFunction) => specialFunction.specialFunctionId !== 22)
                  .map((specialFunction) => specialFunction.specialFunctionId)
              : []
          }
        />
      )}
    </>
  );
};

export default SlidingPaneUnitSpecialFunctions;
